'use client';

import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, ChevronsUpDown, Eye, Pencil, Trash2, CheckSquare, Square, X, UserCheck, CheckCircle, ChevronLeft, ChevronRight,  } from 'lucide-react';
import { StatusBadge, PriorityBadge } from '@/components/ui/StatusBadge';
import type { WorkOrderStatus, PriorityLevel } from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';

// Backend integration point: replace mock data with API call to GET /api/work-orders
interface WorkOrder {
  id: string;
  numero: string;
  tipo: 'Corretiva' | 'Preventiva' | 'Preditiva' | 'Emergencial';
  descricao: string;
  predio: string;
  espaco: string;
  equipamento: string;
  solicitante: string;
  tecnico: string;
  prioridade: PriorityLevel;
  status: WorkOrderStatus;
  dataAbertura: string;
  prazo: string;
  custoEstimado: number;
  tempoDecorrido: string;
}

const mockWorkOrders: WorkOrder[] = [
  {
    id: 'os-001', numero: '#1847', tipo: 'Corretiva',   descricao: 'Ar-condicionado sem refrigeração',
    predio: 'Lab. Ciências',      espaco: 'Lab. Física 3',   equipamento: 'Split 18k BTU — LC-047',
    solicitante: 'Prof. Carvalho', tecnico: 'Fábio Nascimento',  prioridade: 'Crítica',
    status: 'Em Execução', dataAbertura: '17/09/2026', prazo: '17/09/2026', custoEstimado: 1200, tempoDecorrido: '4h 23min',
  },
  {
    id: 'os-002', numero: '#1848', tipo: 'Preventiva',  descricao: 'Manutenção preventiva — sistema hidráulico',
    predio: 'Bloco A — Eng.',     espaco: 'Banheiro 2º andar', equipamento: 'Tubulação principal',
    solicitante: 'Manutenção',    tecnico: 'Marcos Peixoto',    prioridade: 'Média',
    status: 'Atribuída', dataAbertura: '16/09/2026', prazo: '19/09/2026', custoEstimado: 450,  tempoDecorrido: '1d 14h',
  },
  {
    id: 'os-003', numero: '#1849', tipo: 'Corretiva',   descricao: 'Curto-circuito sala 204',
    predio: 'Reitoria',           espaco: 'Sala 204',          equipamento: 'Quadro elétrico QD-04',
    solicitante: 'Secretaria',    tecnico: 'Ana Ferreira',      prioridade: 'Alta',
    status: 'Em Execução', dataAbertura: '17/09/2026', prazo: '17/09/2026', custoEstimado: 800,  tempoDecorrido: '1h 42min',
  },
  {
    id: 'os-004', numero: '#1850', tipo: 'Emergencial', descricao: 'Vazamento no telhado — goteira',
    predio: 'Biblioteca Central', espaco: 'Acervo 1º andar',   equipamento: 'Cobertura — seção B',
    solicitante: 'Prof. Lima',    tecnico: 'Não atribuído',     prioridade: 'Crítica',
    status: 'Aguardando Aprovação', dataAbertura: '17/09/2026', prazo: '17/09/2026', custoEstimado: 3200, tempoDecorrido: '2h 10min',
  },
  {
    id: 'os-005', numero: '#1845', tipo: 'Preventiva',  descricao: 'Troca de luminárias corredor',
    predio: 'Ginásio',            espaco: 'Corredor principal', equipamento: 'Luminárias LED — GI-22',
    solicitante: 'Direção',       tecnico: 'Carlos Mendonça',   prioridade: 'Baixa',
    status: 'Aprovada', dataAbertura: '15/09/2026', prazo: '22/09/2026', custoEstimado: 620,  tempoDecorrido: '2d 3h',
  },
  {
    id: 'os-006', numero: '#1841', tipo: 'Corretiva',   descricao: 'Reparo hidráulico — torneira',
    predio: 'Bloco A — Eng.',     espaco: 'Laboratório 1',     equipamento: 'Torneira industrial',
    solicitante: 'Coord. Eng.',   tecnico: 'Marcos Peixoto',    prioridade: 'Média',
    status: 'Concluída', dataAbertura: '14/09/2026', prazo: '16/09/2026', custoEstimado: 280,  tempoDecorrido: '3d 1h',
  },
  {
    id: 'os-007', numero: '#1844', tipo: 'Preditiva',   descricao: 'Análise vibração — compressor HVAC',
    predio: 'Lab. Ciências',      espaco: 'Casa de máquinas',  equipamento: 'Compressor HVAC-01',
    solicitante: 'Manutenção',    tecnico: 'Carlos Mendonça',   prioridade: 'Alta',
    status: 'Pendente Material', dataAbertura: '13/09/2026', prazo: '18/09/2026', custoEstimado: 1800, tempoDecorrido: '4d 6h',
  },
  {
    id: 'os-008', numero: '#1839', tipo: 'Corretiva',   descricao: 'Porta automática sem funcionamento',
    predio: 'Bloco B — Humanas',  espaco: 'Entrada principal', equipamento: 'Porta automática BH-01',
    solicitante: 'Portaria',      tecnico: 'Ana Ferreira',      prioridade: 'Alta',
    status: 'Aberta', dataAbertura: '12/09/2026', prazo: '15/09/2026', custoEstimado: 950,  tempoDecorrido: '5d 8h',
  },
  {
    id: 'os-009', numero: '#1836', tipo: 'Preventiva',  descricao: 'Revisão semestral — elevadores',
    predio: 'Bloco B — Humanas',  espaco: 'Elevador 1 e 2',    equipamento: 'Elevador BH-EL-01/02',
    solicitante: 'Manutenção',    tecnico: 'Fábio Nascimento',  prioridade: 'Alta',
    status: 'Encerrada', dataAbertura: '08/09/2026', prazo: '12/09/2026', custoEstimado: 4200, tempoDecorrido: '9d 2h',
  },
  {
    id: 'os-010', numero: '#1832', tipo: 'Corretiva',   descricao: 'Elevador — parada entre andares',
    predio: 'Bloco B — Humanas',  espaco: 'Elevador 2',        equipamento: 'Elevador BH-EL-02',
    solicitante: 'Portaria',      tecnico: 'Marcos Peixoto',    prioridade: 'Crítica',
    status: 'Aberta', dataAbertura: '17/09/2026', prazo: '17/09/2026', custoEstimado: 2100, tempoDecorrido: '1h 5min',
  },
  {
    id: 'os-011', numero: '#1831', tipo: 'Corretiva',   descricao: 'Infiltração parede externa',
    predio: 'Reitoria',           espaco: 'Sala de reuniões',  equipamento: 'Parede externa — seção N',
    solicitante: 'Reitoria',      tecnico: 'Priscila Rodrigues', prioridade: 'Média',
    status: 'Em Execução', dataAbertura: '11/09/2026', prazo: '20/09/2026', custoEstimado: 5600, tempoDecorrido: '6d 4h',
  },
  {
    id: 'os-012', numero: '#1828', tipo: 'Preventiva',  descricao: 'Limpeza e revisão — SPDA/Para-raios',
    predio: 'Lab. Ciências',      espaco: 'Cobertura',         equipamento: 'SPDA-LC-01',
    solicitante: 'Manutenção',    tecnico: 'Carlos Mendonça',   prioridade: 'Média',
    status: 'Concluída', dataAbertura: '05/09/2026', prazo: '10/09/2026', custoEstimado: 780,  tempoDecorrido: '12d',
  },
];

type SortKey = keyof WorkOrder;
type SortDir = 'asc' | 'desc' | null;

const allStatuses: WorkOrderStatus[] = [
  'Aberta', 'Aguardando Aprovação', 'Aprovada', 'Atribuída',
  'Em Execução', 'Pendente Material', 'Concluída', 'Encerrada', 'Cancelada',
];
const allPriorities: PriorityLevel[] = ['Crítica', 'Alta', 'Média', 'Baixa'];
const allTipos = ['Corretiva', 'Preventiva', 'Preditiva', 'Emergencial'];
const allPredios = ['Lab. Ciências', 'Bloco A — Eng.', 'Reitoria', 'Biblioteca Central', 'Ginásio', 'Bloco B — Humanas'];

const tipoStyles: Record<string, string> = {
  Corretiva:   'bg-red-50 text-red-700',
  Preventiva:  'bg-blue-50 text-blue-700',
  Preditiva:   'bg-violet-50 text-violet-700',
  Emergencial: 'bg-orange-50 text-orange-700',
};

export default function WorkOrderTableSection() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<WorkOrderStatus | ''>('');
  const [filterPriority, setFilterPriority] = useState<PriorityLevel | ''>('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterPredio, setFilterPredio] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('numero');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [statusDropdownId, setStatusDropdownId] = useState<string | null>(null);
  const [orders, setOrders] = useState<WorkOrder[]>(mockWorkOrders);

  const filtered = useMemo(() => {
    let data = [...orders];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (o) =>
          o.numero.toLowerCase().includes(q) ||
          o.descricao.toLowerCase().includes(q) ||
          o.predio.toLowerCase().includes(q) ||
          o.solicitante.toLowerCase().includes(q) ||
          o.tecnico.toLowerCase().includes(q)
      );
    }
    if (filterStatus) data = data.filter((o) => o.status === filterStatus);
    if (filterPriority) data = data.filter((o) => o.prioridade === filterPriority);
    if (filterTipo) data = data.filter((o) => o.tipo === filterTipo);
    if (filterPredio) data = data.filter((o) => o.predio === filterPredio);
    if (sortDir) {
      data.sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        const cmp = String(av).localeCompare(String(bv), 'pt-BR', { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [orders, search, filterStatus, filterPriority, filterTipo, filterPredio, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground" />;
    if (sortDir === 'asc') return <ChevronUp size={12} className="text-primary" />;
    if (sortDir === 'desc') return <ChevronDown size={12} className="text-primary" />;
    return <ChevronsUpDown size={12} className="text-muted-foreground" />;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === paginated.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map((o) => o.id)));
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      setOrders((prev) => prev.filter((o) => o.id !== deleteTarget));
      toast.success('Ordem de serviço excluída com sucesso.');
    } else {
      const count = selectedIds.size;
      setOrders((prev) => prev.filter((o) => !selectedIds.has(o.id)));
      setSelectedIds(new Set());
      toast.success(`${count} ordens excluídas.`);
    }
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const handleStatusChange = (orderId: string, newStatus: WorkOrderStatus) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
    );
    setStatusDropdownId(null);
    toast.success(`Status atualizado para "${newStatus}".`);
  };

  const handleBulkAssign = () => {
    toast.success(`${selectedIds.size} ordens marcadas para atribuição.`);
    setSelectedIds(new Set());
  };

  const handleBulkApprove = () => {
    const count = selectedIds.size;
    setOrders((prev) =>
      prev.map((o) =>
        selectedIds.has(o.id) && o.status === 'Aguardando Aprovação'
          ? { ...o, status: 'Aprovada' }
          : o
      )
    );
    setSelectedIds(new Set());
    toast.success(`${count} ordens aprovadas.`);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterTipo('');
    setFilterPredio('');
    setPage(1);
  };

  const hasFilters = search || filterStatus || filterPriority || filterTipo || filterPredio;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card border border-border rounded-xl shadow-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por OS, descrição, prédio, técnico..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground"
            />
          </div>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as WorkOrderStatus | ''); setPage(1); }}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/30 text-foreground min-w-[150px]"
          >
            <option value="">Todos os Status</option>
            {allStatuses.map((s) => (
              <option key={`filter-status-${s}`} value={s}>{s}</option>
            ))}
          </select>

          {/* Priority filter */}
          <select
            value={filterPriority}
            onChange={(e) => { setFilterPriority(e.target.value as PriorityLevel | ''); setPage(1); }}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/30 text-foreground min-w-[130px]"
          >
            <option value="">Todas as Prioridades</option>
            {allPriorities.map((p) => (
              <option key={`filter-prio-${p}`} value={p}>{p}</option>
            ))}
          </select>

          {/* Tipo filter */}
          <select
            value={filterTipo}
            onChange={(e) => { setFilterTipo(e.target.value); setPage(1); }}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/30 text-foreground min-w-[130px]"
          >
            <option value="">Todos os Tipos</option>
            {allTipos.map((t) => (
              <option key={`filter-tipo-${t}`} value={t}>{t}</option>
            ))}
          </select>

          {/* Prédio filter */}
          <select
            value={filterPredio}
            onChange={(e) => { setFilterPredio(e.target.value); setPage(1); }}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/30 text-foreground min-w-[180px]"
          >
            <option value="">Todos os Prédios</option>
            {allPredios.map((p) => (
              <option key={`filter-predio-${p}`} value={p}>{p}</option>
            ))}
          </select>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <X size={14} />
              Limpar filtros
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">
              {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="w-10 px-4 py-3">
                  <button onClick={toggleAll} className="flex items-center justify-center">
                    {selectedIds.size === paginated.length && paginated.length > 0 ? (
                      <CheckSquare size={15} className="text-primary" />
                    ) : (
                      <Square size={15} className="text-muted-foreground" />
                    )}
                  </button>
                </th>
                {[
                  { key: 'numero' as SortKey,       label: 'OS #',          w: 'w-20'  },
                  { key: 'tipo' as SortKey,          label: 'Tipo',          w: 'w-28'  },
                  { key: 'descricao' as SortKey,     label: 'Descrição',     w: 'min-w-[180px]' },
                  { key: 'predio' as SortKey,        label: 'Prédio',        w: 'w-36'  },
                  { key: 'espaco' as SortKey,        label: 'Espaço',        w: 'w-32'  },
                  { key: 'solicitante' as SortKey,   label: 'Solicitante',   w: 'w-32'  },
                  { key: 'tecnico' as SortKey,       label: 'Técnico',       w: 'w-36'  },
                  { key: 'prioridade' as SortKey,    label: 'Prioridade',    w: 'w-24'  },
                  { key: 'status' as SortKey,        label: 'Status',        w: 'w-40'  },
                  { key: 'dataAbertura' as SortKey,  label: 'Abertura',      w: 'w-24'  },
                  { key: 'prazo' as SortKey,         label: 'Prazo',         w: 'w-24'  },
                  { key: 'custoEstimado' as SortKey, label: 'Custo Est.',    w: 'w-24'  },
                ].map((col) => (
                  <th
                    key={`th-${col.key}`}
                    className={`${col.w} px-3 py-3 text-left`}
                  >
                    <button
                      onClick={() => toggleSort(col.key)}
                      className="flex items-center gap-1 card-label hover:text-foreground transition-colors duration-150 whitespace-nowrap"
                    >
                      {col.label}
                      <SortIcon col={col.key} />
                    </button>
                  </th>
                ))}
                <th className="w-24 px-3 py-3 text-right card-label">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Search size={20} className="text-muted-foreground" />
                      </div>
                      <p className="text-sm font-600 text-foreground">Nenhuma OS encontrada</p>
                      <p className="text-xs text-muted-foreground max-w-xs">
                        Tente ajustar os filtros ou criar uma nova ordem de serviço.
                      </p>
                      <button
                        onClick={clearFilters}
                        className="text-xs text-primary hover:underline font-600 mt-1"
                      >
                        Limpar filtros
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((order) => {
                  const isSelected = selectedIds.has(order.id);
                  const isOverdue =
                    (order.status === 'Aberta' || order.status === 'Em Execução' || order.status === 'Atribuída') &&
                    order.prazo <= '17/09/2026';

                  return (
                    <tr
                      key={order.id}
                      className={`group transition-colors duration-100 ${
                        isSelected
                          ? 'bg-primary/5'
                          : isOverdue
                          ? 'bg-red-50/40 hover:bg-red-50/70' :'hover:bg-secondary/40'
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-3">
                        <button onClick={() => toggleSelect(order.id)} className="flex items-center justify-center">
                          {isSelected ? (
                            <CheckSquare size={15} className="text-primary" />
                          ) : (
                            <Square size={15} className="text-muted-foreground group-hover:text-foreground" />
                          )}
                        </button>
                      </td>

                      {/* OS # */}
                      <td className="px-3 py-3">
                        <span className="font-700 text-primary tabular-nums text-xs">{order.numero}</span>
                        {isOverdue && (
                          <span className="block text-[10px] text-red-600 font-600 mt-0.5">Atrasada</span>
                        )}
                      </td>

                      {/* Tipo */}
                      <td className="px-3 py-3">
                        <span className={`priority-badge text-[11px] ${tipoStyles[order.tipo]}`}>
                          {order.tipo}
                        </span>
                      </td>

                      {/* Descrição */}
                      <td className="px-3 py-3">
                        <p className="text-foreground font-500 text-xs leading-snug max-w-[200px] truncate" title={order.descricao}>
                          {order.descricao}
                        </p>
                        <p className="text-muted-foreground text-[11px] truncate max-w-[200px]">{order.equipamento}</p>
                      </td>

                      {/* Prédio */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-foreground truncate block max-w-[140px]">{order.predio}</span>
                      </td>

                      {/* Espaço */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-muted-foreground truncate block max-w-[130px]">{order.espaco}</span>
                      </td>

                      {/* Solicitante */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-foreground truncate block max-w-[130px]">{order.solicitante}</span>
                      </td>

                      {/* Técnico */}
                      <td className="px-3 py-3">
                        <span
                          className={`text-xs truncate block max-w-[140px] ${
                            order.tecnico === 'Não atribuído' ? 'text-amber-600 font-600' : 'text-foreground'
                          }`}
                        >
                          {order.tecnico}
                        </span>
                      </td>

                      {/* Prioridade */}
                      <td className="px-3 py-3">
                        <PriorityBadge priority={order.prioridade} />
                      </td>

                      {/* Status — inline dropdown */}
                      <td className="px-3 py-3 relative">
                        <button
                          onClick={() =>
                            setStatusDropdownId(statusDropdownId === order.id ? null : order.id)
                          }
                          className="flex items-center gap-1 group/status"
                          title="Clique para alterar o status"
                        >
                          <StatusBadge status={order.status} />
                          <ChevronDown
                            size={10}
                            className="text-muted-foreground opacity-0 group-hover/status:opacity-100 transition-opacity duration-150 ml-0.5"
                          />
                        </button>

                        {statusDropdownId === order.id && (
                          <div className="absolute left-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-card-lg py-1 min-w-[180px] scale-in">
                            {allStatuses.map((s) => (
                              <button
                                key={`status-opt-${order.id}-${s}`}
                                onClick={() => handleStatusChange(order.id, s)}
                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary transition-colors duration-100 ${
                                  order.status === s ? 'font-700 text-primary' : 'text-foreground'
                                }`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Data Abertura */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-muted-foreground tabular-nums">{order.dataAbertura}</span>
                      </td>

                      {/* Prazo */}
                      <td className="px-3 py-3">
                        <span
                          className={`text-xs tabular-nums font-500 ${
                            isOverdue ? 'text-red-600 font-700' : 'text-foreground'
                          }`}
                        >
                          {order.prazo}
                        </span>
                        <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">{order.tempoDecorrido}</p>
                      </td>

                      {/* Custo */}
                      <td className="px-3 py-3">
                        <span className="text-xs text-foreground tabular-nums font-500">
                          {order.custoEstimado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          <button
                            title="Visualizar OS"
                            className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors duration-150"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            title="Editar OS"
                            className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors duration-150"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            title="Excluir OS — esta ação não pode ser desfeita"
                            onClick={() => {
                              setDeleteTarget(order.id);
                              setDeleteModalOpen(true);
                            }}
                            className="p-1.5 rounded-md hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors duration-150"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/20">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Exibir</span>
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="bg-card border border-border rounded-md px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30"
            >
              {[10, 25, 50].map((n) => (
                <option key={`pp-${n}`} value={n}>{n}</option>
              ))}
            </select>
            <span>por página · {filtered.length} total</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
            >
              <ChevronLeft size={15} className="text-muted-foreground" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
              .reduce<(number | '...')[]>((acc, n, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (n as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...');
                }
                acc.push(n);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                ) : (
                  <button
                    key={`page-${item}`}
                    onClick={() => setPage(item as number)}
                    className={`w-7 h-7 rounded-md text-xs font-600 transition-colors duration-150 ${
                      page === item
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
            >
              <ChevronRight size={15} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 slide-up">
          <div className="bg-foreground text-primary-foreground rounded-xl shadow-card-lg px-5 py-3 flex items-center gap-4">
            <span className="text-sm font-600 tabular-nums">
              {selectedIds.size} OS selecionada{selectedIds.size !== 1 ? 's' : ''}
            </span>
            <div className="w-px h-5 bg-white/20" />
            <button
              onClick={handleBulkApprove}
              className="inline-flex items-center gap-1.5 text-sm font-600 text-emerald-400 hover:text-emerald-300 transition-colors duration-150"
            >
              <CheckCircle size={15} />
              Aprovar
            </button>
            <button
              onClick={handleBulkAssign}
              className="inline-flex items-center gap-1.5 text-sm font-600 text-blue-400 hover:text-blue-300 transition-colors duration-150"
            >
              <UserCheck size={15} />
              Atribuir
            </button>
            <button
              onClick={() => {
                setDeleteTarget(null);
                setDeleteModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 text-sm font-600 text-red-400 hover:text-red-300 transition-colors duration-150"
            >
              <Trash2 size={15} />
              Excluir
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="p-1 rounded hover:bg-white/10 transition-colors duration-150 ml-1"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}
        title="Confirmar exclusão"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            {deleteTarget
              ? 'Tem certeza que deseja excluir esta ordem de serviço? Esta ação não pode ser desfeita.'
              : `Tem certeza que deseja excluir ${selectedIds.size} ordens de serviço? Esta ação não pode ser desfeita.`}
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}
              className="px-4 py-2 text-sm font-600 text-foreground bg-secondary rounded-lg hover:bg-secondary/80 active:scale-95 transition-all duration-150"
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-2 text-sm font-600 text-white bg-red-600 rounded-lg hover:bg-red-700 active:scale-95 transition-all duration-150"
            >
              Excluir
            </button>
          </div>
        </div>
      </Modal>

      {/* Click outside to close status dropdown */}
      {statusDropdownId && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setStatusDropdownId(null)}
        />
      )}
    </div>
  );
}